import { AnalystChartSpec, AnalystTableSpec } from '@digital-twins/shared-types'

import { Result } from '@/components/quick-study-results/quickStudyTypes'
import { isDrawableAnalystPlot } from '@/utils/analystChartPlot'

/**
 * Pull AnalystChart/Table specs off a twin-test finding payload.
 *
 * Cache scope: one-shot, not cached. Pure inspection; no I/O.
 *
 * Step 9 rides specs on the finding result alongside response rows. Field
 * names match Step 6 message persistence (`charts` / `tables`) plus the
 * camelCase aliases a FE-stamped Result uses.
 */

export interface FindingAnalystSpecs {
  charts: Array<AnalystChartSpec>
  tables: Array<AnalystTableSpec>
}

export function isAnalystChartLike(value: unknown): value is AnalystChartSpec {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }
  return (value as AnalystChartSpec).schemaVersion === 'analyst-chart.v1'
}

export function isAnalystTableLike(value: unknown): value is AnalystTableSpec {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }
  return (value as AnalystTableSpec).schemaVersion === 'analyst-table.v1'
}

function collectCharts(value: unknown): Array<AnalystChartSpec> {
  const items = Array.isArray(value) ? value : value == null ? [] : [value]
  return items.filter(isAnalystChartLike)
}

function collectTables(value: unknown): Array<AnalystTableSpec> {
  const items = Array.isArray(value) ? value : value == null ? [] : [value]
  return items.filter(isAnalystTableLike)
}

export function analystSpecsFromPayload(
  payload: Record<string, unknown> | null | undefined,
): FindingAnalystSpecs {
  if (!payload) {
    return { charts: [], tables: [] }
  }

  const stampedCharts = collectCharts(payload.analystCharts)
  const stampedTables = collectTables(payload.analystTables)
  if (stampedCharts.length > 0 || stampedTables.length > 0) {
    return { charts: stampedCharts, tables: stampedTables }
  }

  return {
    charts: collectCharts(
      payload.charts ?? payload.analyst_charts ?? payload.chart ?? payload.analystChart,
    ),
    tables: collectTables(
      payload.tables ?? payload.analyst_tables ?? payload.table ?? payload.analystTable,
    ),
  }
}

export function analystSpecsFromResult(result: Result | Record<string, unknown>): FindingAnalystSpecs {
  return analystSpecsFromPayload(result as Record<string, unknown>)
}

export function hasFindingAnalystSpec(specs: FindingAnalystSpecs): boolean {
  return specs.charts.length > 0 || specs.tables.length > 0
}

/**
 * Spec-preferred default exhibit. Interactive cut-switching stays on the
 * client-rows path — spec-per-cut is a follow-up.
 */
export function shouldPreferAnalystSpec(
  specs: FindingAnalystSpecs,
  selectedCuts?: Array<string>,
): boolean {
  if ((selectedCuts?.length ?? 0) > 0) {
    return false
  }
  return hasFindingAnalystSpec(specs)
}

/** The chart specs that read the question itself (the producer tags them by `subtitle`). */
const READ_CHART_TAGS = new Set(['rating_scale', 'distribution', 'ranking'])

/**
 * W7 Q1: whether every read chart spec carries Python's plot (drawn through the analyst
 * plot, no browser bands). A read with a stamped study plot draws that plot instead of
 * specs that do not (a plotless distribution spec would draw five browser bands).
 * Crosstab charts (`crosstab:*`) and tables are not the read and do not count.
 */
export function findingSpecsCarryPlots(specs: FindingAnalystSpecs): boolean {
  return specs.charts
    .filter((chart) => READ_CHART_TAGS.has(chart.subtitle ?? ''))
    .every((chart) => isDrawableAnalystPlot(chart.plot))
}
